import { useCallback, useEffect, useMemo, useState } from "react";
import {
  List,
  TextField,
  WrapperField,
  DatagridConfigurable,
  WithRecord,
  DateField,
  FunctionField,
  BulkDeleteWithConfirmButton,
  useTheme,
  useTranslate,
  usePermissions,
  useGetIdentity,
} from "react-admin";
import { useLocation, useNavigate } from "react-router-dom";

import SaleOrderAddDialog from "../../components/saleOrders/create/SaleOrderAddDialog";
import SaleOrderEditDialog from "../../components/saleOrders/edit/SaleOrderEditDialog";
import saleOrderFilterList from "../../components/saleOrders/filter/saleOrderFilterList";
import TableListActions from "../../components/tableListActions/TableListActions";
import SaleOrderExpandList from "../../components/saleOrders/expand/SaleOrderExpandList";
import EditBtn from "../../components/editBtn/EditBtn";
import PrintBtn from "../../components/printBtn/PrintBtn";
import PrintHandler from "../../components/printHandler/PrintHandler";
import PrintInvoiceContent from "../../components/saleOrders/print/invoice/PrintInvoiceContent";
import ResetFilterButton from "../../components/tableListActions/ResetFilterButton";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const normalizeRangeFilter = (filter) => {
  const nextFilter = { ...filter };
  if (nextFilter.date) {
    nextFilter.start_date = nextFilter.start_date || nextFilter.date;
    nextFilter.end_date = nextFilter.end_date || nextFilter.date;
    delete nextFilter.date;
  }
  return nextFilter;
};

const Aside = () => {
  const t = useTranslate();
  const menu = t("resources.inventory_pending.name").split(" |||| ");
  return (
    <div style={{ width: 200, margin: "1em" }}>
      <h5>
        Saat SO dibuat maka produk sejumlah quantity yang dipesan akan tertahan
        tidak bisa diambil untuk pesanan lain sampai SO tersebut dihapus atau
        dibuat SJ nya dan dikirim sebagian, maka sebagian lagi bisa dibatalkan
        melalui menu {menu[1] ? menu[1] : menu[0]}. SO tidak dapat dihapus
        ketika sudah dibuat SJ nya meskipun SJ tersebut belum di approve, jika
        ingin menghapus SO maka perlu hapus dahulu SJ yang belum approved
        tersebut
      </h5>
    </div>
  );
};

const SaleOrderList = () => {
  const { permissions } = usePermissions();
  const { data: identity } = useGetIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [recordId, setRecordId] = useState();
  const [showAside, setShowAside] = useState(false);
  const [theme] = useTheme("light");
  // ✅ STATE PRINT (WAJIB ADA)
  const [printInvoiceRecord, setPrintInvoiceRecord] = useState({});
  const [resetPrintInvoiceContent, setResetPrintInvoiceContent] = useState();

  const currentCompanyFilter = useMemo(
    () => (identity?.companyID ? { company_id: identity.companyID } : {}),
    [identity?.companyID]
  );

  const locationFilter = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    try {
      return normalizeRangeFilter(JSON.parse(searchParams.get("filter") || "{}"));
    } catch {
      return {};
    }
  }, [location.search]);

  useEffect(() => {
    if (!currentCompanyFilter.company_id) return;
    if (locationFilter.company_id === currentCompanyFilter.company_id) return;

    const searchParams = new URLSearchParams(location.search);
    searchParams.set(
      "filter",
      JSON.stringify({
        ...locationFilter,
        company_id: currentCompanyFilter.company_id,
      })
    );

    navigate(
      {
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      },
      { replace: true }
    );
  }, [
    currentCompanyFilter.company_id,
    location.pathname,
    location.search,
    locationFilter,
    navigate,
  ]);

  const filterDefaultValues = useMemo(
    () => ({
      start_date: firstDate,
      end_date: lastDate,
      ...currentCompanyFilter,
    }),
    [currentCompanyFilter]
  );

  const parentFilter = useMemo(
    () => ({
      ...locationFilter,
      ...(currentCompanyFilter.company_id
        ? { company_id: currentCompanyFilter.company_id }
        : {}),
      start_date: locationFilter.start_date || firstDate,
      end_date: locationFilter.end_date || lastDate,
    }),
    [currentCompanyFilter.company_id, locationFilter]
  );

  const handleClickOpenAdd = useCallback(() => {
    setOpenAdd(true);
  }, []);

  const handleClickOpenEdit = useCallback(
    (recId) => (e) => {
      e.stopPropagation();
      setRecordId(recId);
      setOpenEdit(true);
    },
    []
  );

  const rowClickHandler = useCallback(() => {
    setShowAside((prev) => !prev);
  }, []);

  const list = useMemo(
    () => (
      <List
        title="mine.list_title.sale_order"
        storeKey={false}
        empty={false}
        aside={showAside ? <Aside /> : null}
        actions={
          <TableListActions
            createHandler={handleClickOpenAdd}
            exporter={false}
            createPermission="create_sale_order"
            extraActions={
              <ResetFilterButton defaultFilters={filterDefaultValues} />
            }
          />
        }
        filters={saleOrderFilterList(permissions)}
        filter={parentFilter}
        filterDefaultValues={filterDefaultValues}
        sort={{ field: "date", order: "DESC" }}
        queryOptions={{ meta: { sort: ["date DESC", "code DESC"] } }}
      >
        <DatagridConfigurable
          bulkActionButtons={<BulkDeleteWithConfirmButton />}
          rowClick={rowClickHandler}
          expand={<SaleOrderExpandList />}
          sx={{
            "& .RaDatagrid-expandedPanel > td": {
              p: 0,
              bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
            },
          }}
        >
          <TextField label="mine.label.depo" source="company.alias" />

          <TextField source="code" label="mine.label.receipt_no" />

          <DateField
            source="date"
            label="mine.label.date"
            options={{
              year: "numeric",
              month: "short",
              day: "2-digit",
            }}
          />

          <FunctionField
            label="mine.label.customer"
            render={(record) =>
              `${record.customer.name} (${record.customer.code})`
            }
          />

          <FunctionField
            label="Salesman"
            render={(record) =>
              `${record.salesman.name} (${record.salesman.code})`
            }
          />

          <WrapperField label="mine.label.actions" textAlign="center">
            <WithRecord
              render={(record) =>
                !record.approved && (
                  <>
                    <EditBtn
                      editHandler={handleClickOpenEdit(record.id)}
                      variant="outlined"
                      sx={{
                        width: "fit-content",
                        mr: 1,
                      }}
                    />

                    <PrintBtn
                      recordId={record.id}
                      setPrintRecord={setPrintInvoiceRecord}
                      setResetPrintContent={setResetPrintInvoiceContent}
                      btnProps={{
                        label: "Invoice",
                        sx: { mx: 0.5 },
                      }}
                      plateNumber={record.transport?.plate}
                    />
                  </>
                )
              }
            />
          </WrapperField>
        </DatagridConfigurable>
      </List>
    ),
    [
      filterDefaultValues,
      handleClickOpenAdd,
      handleClickOpenEdit,
      parentFilter,
      permissions,
      rowClickHandler,
      showAside,
      theme,
    ]
  );

  return (
    <>
      {list}

      <SaleOrderAddDialog open={openAdd} setOpen={setOpenAdd} />
      <SaleOrderEditDialog
        open={openEdit}
        setOpen={setOpenEdit}
        recordId={recordId}
      />

      {/* ✅ PRINT HANDLER SELALU ADA */}
      <PrintHandler
        record={printInvoiceRecord}
        resetPrintContent={resetPrintInvoiceContent}
        Content={PrintInvoiceContent}
      />
    </>
  );
};

export default SaleOrderList;
